import { NgModule } from '@angular/core';
import { Observable } from 'rxjs';

import { ConsoleLog } from './business/console-log';
import { PageInformation } from './business/page-information';
import { EnvUtils } from './env.utils';
import { ConsoleService } from './services/console.service';
import { RxService } from './services/rx.service';
import { EcmUrlSanitizerUtil } from './utils/ecm-url-sanitizer.util';

@NgModule({
    imports: [],
    declarations: [],
    bootstrap: [],
    providers: [],
    exports: [],
})
export class EcmDispatcherModule {
    public static sendPageLoaded(info: PageInformation): void {
        RxService.getPageLoadedStore().next(info);
    }

    public static get pageLoadedEvents(): Observable<any> {
        return RxService.getPageLoadedStore().asObservable();
    }
    /**
     * Sends message through dispatcher
     * @param {string} message
     */
    public static sendMessage(message: string): void {
        RxService.getMessagesStore().next(message);
    }

    /**
     * Subscribes to dispatcher message
     * @param {Function} callback
     */
    // eslint-disable-next-line @typescript-eslint/ban-types
    public static onMessage(callback: Function): void {
        RxService.getMessagesStore().subscribe((nextValue: string) => {
            callback(nextValue);
        });
    }

    /**
     * Sends contract opened through dispatcher
     * @param {any} contract object with contract data, model :
     *              {
     *                  {string} cid: <contract_identifier>, // mandatory
     *                  {string} brand: "orange|sosh", // mandatory (from contractSelector.brand)
     *                  {string} type: <contract_type> // mandatory (from contractSelector.type)
     *                  {string} offer: <contract_offer> // mandatory (from contractSelector.offerName)
     *              }
     */
    public static sendContractOpened(contract: any): void {
        RxService.getOpenedContractStore().next(contract);
    }

    /**
     * Subscribes to dispatcher contract open event
     * @param {Function} callback
     */
    // eslint-disable-next-line @typescript-eslint/ban-types
    public static onContractOpened(callback: Function): void {
        RxService.getOpenedContractStore().subscribe((nextValue: any) => {
            callback(nextValue);
        });
    }

    /**
     * Sends change elco header through dispatcher
     * @param {boolean} showHeader show or not the elco header :
     */
    // eslint-disable-next-line @typescript-eslint/ban-types
    public static sendShowElcoHeader(showHeader: boolean): void {
        RxService.getElcoGenericHeaderViewStore().next(showHeader);
    }

    /**
     * Subscribes to dispatcher elco header change event
     * @param {Function} callback
     */
    // eslint-disable-next-line @typescript-eslint/ban-types
    public static onShowElcoHeader(callback: Function): void {
        RxService.getElcoGenericHeaderViewStore().subscribe((nextValue: boolean) => {
            callback(nextValue);
        });
    }

    /**
     * Sends customer mapping opened through dispatcher
     * @param {any} customerMapping object with customer mapping data, model :
     *              {
     *                  {string} pid: <customer_identifier>, // mandatory
     *                  {string[]} customerCids: Array of <contractId> // mandatory
     *              }
     */
    public static sendCustomerMappingOpened(customerMapping: any): void {
        RxService.getOpenedCustomerMappingStore().next(customerMapping);
    }

    /**
     * Subscribes to dispatcher customer mapping open event
     * @param {Function} callback
     */
    // eslint-disable-next-line @typescript-eslint/ban-types
    public static onCustomerMappingOpened(callback: Function): void {
        RxService.getOpenedCustomerMappingStore().subscribe((nextValue: any) => {
            callback(nextValue);
        });
    }

    /**
     * Sends message from nec through dispatcher
     * @param {any} necFeMessage object  :
     *              {
     *                  {string} idContrat: <contract_identifier>, // mandatory
     *                  {string} idUsage: <page_usage_identifier>, // mandatory
     *                  {string} currentUrl : <necFe_url> // mandatory
     *              }
     */
    public static sendMessageFromNec(necFeMessage: any): void {
        RxService.getMessageFromNecStore().next(necFeMessage);
    }

    /**
     * Subscribes to dispatcher message from nec
     * @param {Function} callback
     */
    // eslint-disable-next-line @typescript-eslint/ban-types
    public static onMessageFromNec(callback: Function): void {
        RxService.getMessageFromNecStore().subscribe((nextValue: any) => {
            callback(nextValue);
        });
    }

    /**
     * Sends title through dispatcher
     * @param {string} title
     */
    public static sendTitle(title: string): void {
        RxService.getTitleStore().next(title);
    }

    /**
     * Subscribes to dispatcher title event
     * @param {Function} callback
     */
    // eslint-disable-next-line @typescript-eslint/ban-types
    public static onTitle(callback: Function): void {
        RxService.getTitleStore().subscribe((nextValue: string) => {
            callback(nextValue);
        });
    }

    /**
     * Sends error through dispatcher
     * @param {any} error object with error data, model :
     *              {
     *                  {string} type: "info|warning|error|unauthorized", // mandatory
     *                  {string} title: <error_title>, // mandatory if type is "info|warning|error"
     *                  {string} description: <error_description>
     *              }
     */
    public static sendError(error: any): void {
        RxService.getErrorStore().next(error);
    }

    /**
     * Subscribes to dispatcher error event
     * @param {Function} callback
     */
    // eslint-disable-next-line @typescript-eslint/ban-types
    public static onError(callback: Function): void {
        RxService.getErrorStore().subscribe((nextValue: any) => {
            callback(nextValue);
        });
    }

    public static handlePreviousPageLink(routing: string): string {
        return EcmUrlSanitizerUtil.handleAndSanitizeRoutingUri(routing);
    }

    /**
     * Sends back route through dispatcher
     * @param {string} routing
     */
    public static sendBackRoute(routing: string): void {
        const previousPageRouting = EcmDispatcherModule.handlePreviousPageLink(routing);
        RxService.getBackRouteStore().next(previousPageRouting);
    }

    /**
     * Get the back route
     * @return the back route as observable
     */
    public static getBackRoute(): Observable<string> {
        return RxService.getBackRouteStore().asObservable();
    }

    /**
     * Subscribes to dispatcher back route event
     * @param {Function} callback
     */
    // eslint-disable-next-line @typescript-eslint/ban-types
    public static onBackRoute(callback: Function): void {
        RxService.getBackRouteStore().subscribe((nextValue: string) => {
            callback(nextValue);
        });
    }

    /**
     * Sends survey params through dispatcher
     * @param {any} surveyParams
     */
    public static sendSurveyParams(surveyParams: any): void {
        RxService.getSurveyParamsStore().next(surveyParams);
    }

    /**
     * Subscribes to dispatcher survey params
     * @param {Function} callback
     */
    // eslint-disable-next-line @typescript-eslint/ban-types
    public static onSurveyParams(callback: Function): void {
        RxService.getSurveyParamsStore().subscribe((nextValue: any) => {
            callback(nextValue);
        });
    }

    /**
     * Sends console debug log through dispatcher
     * @param {string} className Name of class writing the log
     * @param {string} message ConsoleLog message to display
     */
    public static sendConsoleDebug(className: string, message: string): void {
        this.sendConsoleLog('debug', className, message);
    }

    /**
     * Sends console info log through dispatcher
     * @param {string} className Name of class writing the log
     * @param {string} message ConsoleLog message to display
     */
    public static sendConsoleInfo(className: string, message: string): void {
        this.sendConsoleLog('info', className, message);
    }

    /**
     * Sends console error log through dispatcher
     * @param {string} className Name of class writing the log
     * @param {string} message ConsoleLog message to display
     */
    public static sendConsoleError(className: string, message: string): void {
        this.sendConsoleLog('error', className, message);
    }

    /**
     * Sends change menu bar through dispatcher
     * @param {boolean} showMenu show or not the sticky menu :
     * @deprecated use sendShowMenuBar instead
     */
    public static sendShowStickyMenu(showMenu: boolean): void {
        EcmDispatcherModule.sendShowMenuBar(showMenu);
    }

    /**
     * Sends change menu bar through dispatcher
     * @param {boolean} showMenu show or not the menu bar :
     */
    public static sendShowMenuBar(showMenu: boolean): void {
        RxService.getMenuBarViewStore().next(showMenu);
    }

    /**
     * Subscribes to dispatcher menu bar change event
     * @param {Function} callback
     */
    // eslint-disable-next-line @typescript-eslint/ban-types
    public static onShowMenuBar(callback: Function): void {
        RxService.getMenuBarViewStore().subscribe((nextValue: boolean) => {
            callback(nextValue);
        });
    }

    /**
     * Performs console log event dispatch (only in dev mode)
     * @param {string} level Possible values : "debug|info|error"
     * @param {string} className Name of class writing the log
     * @param {string} message ConsoleLog message to display
     */
    private static sendConsoleLog(level: string, className: string, message: string): void {
        if (!EnvUtils.production) {
            const log: ConsoleLog = new ConsoleLog(level, className, message);
            ConsoleService.log(log);
        }
    }
}
